import * as React from 'react';
import { DetailedHTMLProps, HTMLAttributes, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { mergeClasses } from '@tcl-argon-utils/utils';
import { useCollapseStyles } from './styles';
import { ChevronDown } from '@tcl-argon-icons/icons';

type DivElement = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export interface CollapseProps extends Omit<DivElement, 'onChange'> {
  /**String representing the header of what is to be displayed in the toggled content */
  title?: string;
  /** Map of rule names and generated class names */
  classes?: Record<string, string>;
  /** A handler triggered whenever this toggle is toggled by mouse or keyboard input. */
  onChange?: (value: boolean) => void;
  /** Content that will be shown when collapse component is open */
  children: JSX.Element;
  /** If `true`, apply show the JSX Element. If `false`, then hide the JSX Element. */
  isOpen?: boolean;
  /** Controls whether this component's title appear to the left or to the right of the toggle. */
  titlePlacement?: 'left' | 'right';
}

export const Collapse: React.FC<CollapseProps> = ({
  classes: classOverrides,
  className,
  title,
  isOpen = false,
  titlePlacement = 'left',
  children,
  onChange = () => {},
  ...props
}) => {
  const classes = mergeClasses(useCollapseStyles({ isOpen }), classOverrides, {
    root: className,
  });

  const [height, setHeight] = useState('0px');

  const computedClassName = isOpen ? classes.openToggle : classes.closeToggle;
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setHeight(isOpen ? `${contentRef.current.scrollHeight}px` : '0px');
  }, [isOpen, children]);

  const onClick = () => {
    onChange(isOpen);
  };

  return (
    <div className={cn(classes.root)} data-analytics="collapse" {...props}>
      <div className={classes.titleWrapper} onClick={onClick}>
        {titlePlacement === 'left' && <span className={classes.title}>{title}</span>}
        <button className={classes.toggle}>
          <ChevronDown className={computedClassName}></ChevronDown>
        </button>
        {titlePlacement === 'right' && <span className={classes.title}>{title}</span>}
      </div>
      <div className={classes.contentWrapper}>
        <div
          ref={contentRef}
          className={classes.content}
          style={{
            maxHeight: height,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
